// Debounced auto-save for the screw-shear-connector calculation, mirroring
// useAutoSaveBeamReinforcementConfiguration.ts. CalculationInput (imported from the pure
// calc engine, see formulas/types.ts) already matches the screw_connector.calculations SQL
// columns/Go DTO 1:1 (see internal/engines/screw_connector/models/api/calculation.go) — the
// only addition here is `unitSystem`, which exists on the DTO but not on CalculationInput
// (the engine itself is unit-agnostic; this module only ever operates in metric, matching
// the source tool, so there is no unit-of-measure toggle in the UI).
import { useEffect, useRef, useState } from "react";
import { Control, useWatch } from "react-hook-form";
import type { CalculationInput } from "./formulas/types";
import { Calculation, UpdateCalculationBody } from "../types/calculations";
import { useCalculationsApi } from "../useCalculations";

export type ScrewConnectorCalculationData = CalculationInput & { unitSystem: string };
export type ScrewConnectorCalculationFormValues = ScrewConnectorCalculationData;

const keepDefined = <T>(value: T | undefined, fallback: T): T => (value === undefined ? fallback : value);

// θ (element 1 angle, degrees) of a new calculation, also where the slider sits while the box is emptied: the
// backend's default too (defaultTheta in internal/engines/screw_connector/services/calculation_service.go).
export const THETA_DEFAULT = 60;

export function buildScrewConnectorCalculationDefaults(
  calculation?: Calculation<ScrewConnectorCalculationData>,
): ScrewConnectorCalculationFormValues {
  const d = calculation?.data;

  return {
    unitSystem: d?.unitSystem ?? "metric",

    // member sizes and forces start empty, like the source tool's required inputs (the engine blocks the
    // calculation until they are filled in); theta at the slider's default.
    theta: d?.theta ?? THETA_DEFAULT,
    connectionType: d?.connectionType ?? "legno",

    element1TimberMaterialId: d?.element1TimberMaterialId ?? null,
    element1CustomMaterialName: d?.element1CustomMaterialName ?? null,
    element1CharacteristicDensity: d?.element1CharacteristicDensity ?? null,
    element1MeanDensity: d?.element1MeanDensity ?? null,
    element1FiberOrientation: d?.element1FiberOrientation ?? 0,
    element1ScrewGrainAngle: d?.element1ScrewGrainAngle ?? 90,
    element1ScrewGrainAngleAuto: d?.element1ScrewGrainAngleAuto ?? true,
    element1Continuous: d?.element1Continuous ?? false,
    element1Width: d?.element1Width ?? null,
    element1Depth: d?.element1Depth ?? null,
    element1Thickness: d?.element1Thickness ?? null,
    element1DouglasFir: d?.element1DouglasFir ?? false,
    element1ReduceUnloadedEdgeDistance: d?.element1ReduceUnloadedEdgeDistance ?? false,
    element1EdgeDistanceA1e: d?.element1EdgeDistanceA1e ?? null,
    element1EdgeDistanceA2e: d?.element1EdgeDistanceA2e ?? null,
    element1EdgeDistanceA3t: d?.element1EdgeDistanceA3t ?? null,
    element1EdgeDistanceA4t: d?.element1EdgeDistanceA4t ?? null,
    element1EdgeDistanceA4c: d?.element1EdgeDistanceA4c ?? null,

    // CLT controls start at the source tool's initial values (element 1 only ever allows 'wide').
    element1CltLato: d?.element1CltLato ?? "wide",
    element1CltNumStrati: d?.element1CltNumStrati ?? 3,
    element1CltDisposizione: d?.element1CltDisposizione ?? "fisso",
    element1CltSpessoriMode: d?.element1CltSpessoriMode ?? "uniforme",
    element1CltSpessore0: d?.element1CltSpessore0 ?? 20,
    element1CltSpessore90: d?.element1CltSpessore90 ?? 20,
    element1CltClasse: d?.element1CltClasse ?? "C24",
    element1CltLayers: d?.element1CltLayers ?? null,

    element2TimberMaterialId: d?.element2TimberMaterialId ?? null,
    element2CustomMaterialName: d?.element2CustomMaterialName ?? null,
    element2CharacteristicDensity: d?.element2CharacteristicDensity ?? null,
    element2MeanDensity: d?.element2MeanDensity ?? null,
    element2FiberOrientation: d?.element2FiberOrientation ?? 0,
    element2ScrewGrainAngle: d?.element2ScrewGrainAngle ?? 90,
    element2ScrewGrainAngleAuto: d?.element2ScrewGrainAngleAuto ?? true,
    element2Continuous: d?.element2Continuous ?? false,
    element2Width: d?.element2Width ?? null,
    element2Depth: d?.element2Depth ?? null,
    element2Thickness: d?.element2Thickness ?? null,
    element2DouglasFir: d?.element2DouglasFir ?? false,
    element2ReduceUnloadedEdgeDistance: d?.element2ReduceUnloadedEdgeDistance ?? false,
    element2EdgeDistanceA1e: d?.element2EdgeDistanceA1e ?? null,
    element2EdgeDistanceA2e: d?.element2EdgeDistanceA2e ?? null,
    element2EdgeDistanceA3t: d?.element2EdgeDistanceA3t ?? null,
    element2EdgeDistanceA3c: d?.element2EdgeDistanceA3c ?? null,
    element2EdgeDistanceA4t: d?.element2EdgeDistanceA4t ?? null,
    element2EdgeDistanceA4c: d?.element2EdgeDistanceA4c ?? null,

    element2CltLato: d?.element2CltLato ?? "narrow",
    element2CltNumStrati: d?.element2CltNumStrati ?? 3,
    element2CltDisposizione: d?.element2CltDisposizione ?? "fisso",
    element2CltSpessoriMode: d?.element2CltSpessoriMode ?? "uniforme",
    element2CltSpessore0: d?.element2CltSpessore0 ?? 20,
    element2CltSpessore90: d?.element2CltSpessore90 ?? 20,
    element2CltClasse: d?.element2CltClasse ?? "C24",
    element2CltLayers: d?.element2CltLayers ?? null,

    forceFn: d?.forceFn ?? null,
    forceFv: d?.forceFv ?? null,
    forceFa: d?.forceFa ?? null,
    serviceClassCode: d?.serviceClassCode ?? 2,
    loadDurationClassCode: d?.loadDurationClassCode ?? "medium_term",
    normativeStandard: d?.normativeStandard ?? "EC5",
    gammaMColumn: d?.gammaMColumn ?? "A",
    gammaMAuto: d?.gammaMAuto ?? true,
    gammaMValue: d?.gammaMValue ?? null,

    screwId: d?.screwId ?? null,
    preDrilled: d?.preDrilled ?? true,
    washerCode: d?.washerCode ?? null,
    autoSpacing: d?.autoSpacing ?? true,

    gridRows: d?.gridRows ?? 3,
    gridCols: d?.gridCols ?? 2,
    spacingS1: d?.spacingS1 ?? null,
    spacingS2: d?.spacingS2 ?? null,
    gridOffsetX: d?.gridOffsetX ?? 0,
    gridOffsetY: d?.gridOffsetY ?? 0,
    staggerRows: d?.staggerRows ?? false,
    staggerTwoSides: d?.staggerTwoSides ?? false,
    spreadScrews: d?.spreadScrews ?? true,
    limitGridToMax: d?.limitGridToMax ?? true,
    limitGridDiagonalAtGrazingAngle: d?.limitGridDiagonalAtGrazingAngle ?? true,
    gridRowsLinkedToMax: d?.gridRowsLinkedToMax ?? true,
    gridColsLinkedToMax: d?.gridColsLinkedToMax ?? true,
    gridOffsetLinkedToMax: d?.gridOffsetLinkedToMax ?? true,
  };
}

const mergeScrewConnectorCalculationValues = (
  values: Partial<ScrewConnectorCalculationFormValues> | undefined,
  fallback: ScrewConnectorCalculationFormValues,
): ScrewConnectorCalculationFormValues => ({
  unitSystem: keepDefined(values?.unitSystem, fallback.unitSystem),

  theta: keepDefined(values?.theta, fallback.theta),
  connectionType: keepDefined(values?.connectionType, fallback.connectionType),

  element1TimberMaterialId: keepDefined(values?.element1TimberMaterialId, fallback.element1TimberMaterialId),
  element1CustomMaterialName: keepDefined(values?.element1CustomMaterialName, fallback.element1CustomMaterialName),
  element1CharacteristicDensity: keepDefined(
    values?.element1CharacteristicDensity,
    fallback.element1CharacteristicDensity,
  ),
  element1MeanDensity: keepDefined(values?.element1MeanDensity, fallback.element1MeanDensity),
  element1FiberOrientation: keepDefined(values?.element1FiberOrientation, fallback.element1FiberOrientation),
  element1ScrewGrainAngle: keepDefined(values?.element1ScrewGrainAngle, fallback.element1ScrewGrainAngle),
  element1ScrewGrainAngleAuto: keepDefined(values?.element1ScrewGrainAngleAuto, fallback.element1ScrewGrainAngleAuto),
  element1Continuous: keepDefined(values?.element1Continuous, fallback.element1Continuous),
  element1Width: keepDefined(values?.element1Width, fallback.element1Width),
  element1Depth: keepDefined(values?.element1Depth, fallback.element1Depth),
  element1Thickness: keepDefined(values?.element1Thickness, fallback.element1Thickness),
  element1DouglasFir: keepDefined(values?.element1DouglasFir, fallback.element1DouglasFir),
  element1ReduceUnloadedEdgeDistance: keepDefined(
    values?.element1ReduceUnloadedEdgeDistance,
    fallback.element1ReduceUnloadedEdgeDistance,
  ),
  element1EdgeDistanceA1e: keepDefined(values?.element1EdgeDistanceA1e, fallback.element1EdgeDistanceA1e),
  element1EdgeDistanceA2e: keepDefined(values?.element1EdgeDistanceA2e, fallback.element1EdgeDistanceA2e),
  element1EdgeDistanceA3t: keepDefined(values?.element1EdgeDistanceA3t, fallback.element1EdgeDistanceA3t),
  element1EdgeDistanceA4t: keepDefined(values?.element1EdgeDistanceA4t, fallback.element1EdgeDistanceA4t),
  element1EdgeDistanceA4c: keepDefined(values?.element1EdgeDistanceA4c, fallback.element1EdgeDistanceA4c),

  element1CltLato: keepDefined(values?.element1CltLato, fallback.element1CltLato),
  element1CltNumStrati: keepDefined(values?.element1CltNumStrati, fallback.element1CltNumStrati),
  element1CltDisposizione: keepDefined(values?.element1CltDisposizione, fallback.element1CltDisposizione),
  element1CltSpessoriMode: keepDefined(values?.element1CltSpessoriMode, fallback.element1CltSpessoriMode),
  element1CltSpessore0: keepDefined(values?.element1CltSpessore0, fallback.element1CltSpessore0),
  element1CltSpessore90: keepDefined(values?.element1CltSpessore90, fallback.element1CltSpessore90),
  element1CltClasse: keepDefined(values?.element1CltClasse, fallback.element1CltClasse),
  element1CltLayers: keepDefined(values?.element1CltLayers, fallback.element1CltLayers),

  element2TimberMaterialId: keepDefined(values?.element2TimberMaterialId, fallback.element2TimberMaterialId),
  element2CustomMaterialName: keepDefined(values?.element2CustomMaterialName, fallback.element2CustomMaterialName),
  element2CharacteristicDensity: keepDefined(
    values?.element2CharacteristicDensity,
    fallback.element2CharacteristicDensity,
  ),
  element2MeanDensity: keepDefined(values?.element2MeanDensity, fallback.element2MeanDensity),
  element2FiberOrientation: keepDefined(values?.element2FiberOrientation, fallback.element2FiberOrientation),
  element2ScrewGrainAngle: keepDefined(values?.element2ScrewGrainAngle, fallback.element2ScrewGrainAngle),
  element2ScrewGrainAngleAuto: keepDefined(values?.element2ScrewGrainAngleAuto, fallback.element2ScrewGrainAngleAuto),
  element2Continuous: keepDefined(values?.element2Continuous, fallback.element2Continuous),
  element2Width: keepDefined(values?.element2Width, fallback.element2Width),
  element2Depth: keepDefined(values?.element2Depth, fallback.element2Depth),
  element2Thickness: keepDefined(values?.element2Thickness, fallback.element2Thickness),
  element2DouglasFir: keepDefined(values?.element2DouglasFir, fallback.element2DouglasFir),
  element2ReduceUnloadedEdgeDistance: keepDefined(
    values?.element2ReduceUnloadedEdgeDistance,
    fallback.element2ReduceUnloadedEdgeDistance,
  ),
  element2EdgeDistanceA1e: keepDefined(values?.element2EdgeDistanceA1e, fallback.element2EdgeDistanceA1e),
  element2EdgeDistanceA2e: keepDefined(values?.element2EdgeDistanceA2e, fallback.element2EdgeDistanceA2e),
  element2EdgeDistanceA3t: keepDefined(values?.element2EdgeDistanceA3t, fallback.element2EdgeDistanceA3t),
  element2EdgeDistanceA3c: keepDefined(values?.element2EdgeDistanceA3c, fallback.element2EdgeDistanceA3c),
  element2EdgeDistanceA4t: keepDefined(values?.element2EdgeDistanceA4t, fallback.element2EdgeDistanceA4t),
  element2EdgeDistanceA4c: keepDefined(values?.element2EdgeDistanceA4c, fallback.element2EdgeDistanceA4c),

  element2CltLato: keepDefined(values?.element2CltLato, fallback.element2CltLato),
  element2CltNumStrati: keepDefined(values?.element2CltNumStrati, fallback.element2CltNumStrati),
  element2CltDisposizione: keepDefined(values?.element2CltDisposizione, fallback.element2CltDisposizione),
  element2CltSpessoriMode: keepDefined(values?.element2CltSpessoriMode, fallback.element2CltSpessoriMode),
  element2CltSpessore0: keepDefined(values?.element2CltSpessore0, fallback.element2CltSpessore0),
  element2CltSpessore90: keepDefined(values?.element2CltSpessore90, fallback.element2CltSpessore90),
  element2CltClasse: keepDefined(values?.element2CltClasse, fallback.element2CltClasse),
  element2CltLayers: keepDefined(values?.element2CltLayers, fallback.element2CltLayers),

  forceFn: keepDefined(values?.forceFn, fallback.forceFn),
  forceFv: keepDefined(values?.forceFv, fallback.forceFv),
  forceFa: keepDefined(values?.forceFa, fallback.forceFa),
  serviceClassCode: keepDefined(values?.serviceClassCode, fallback.serviceClassCode),
  loadDurationClassCode: keepDefined(values?.loadDurationClassCode, fallback.loadDurationClassCode),
  normativeStandard: keepDefined(values?.normativeStandard, fallback.normativeStandard),
  gammaMColumn: keepDefined(values?.gammaMColumn, fallback.gammaMColumn),
  gammaMAuto: keepDefined(values?.gammaMAuto, fallback.gammaMAuto),
  gammaMValue: keepDefined(values?.gammaMValue, fallback.gammaMValue),

  screwId: keepDefined(values?.screwId, fallback.screwId),
  preDrilled: keepDefined(values?.preDrilled, fallback.preDrilled),
  washerCode: keepDefined(values?.washerCode, fallback.washerCode),
  autoSpacing: keepDefined(values?.autoSpacing, fallback.autoSpacing),

  gridRows: keepDefined(values?.gridRows, fallback.gridRows),
  gridCols: keepDefined(values?.gridCols, fallback.gridCols),
  spacingS1: keepDefined(values?.spacingS1, fallback.spacingS1),
  spacingS2: keepDefined(values?.spacingS2, fallback.spacingS2),
  gridOffsetX: keepDefined(values?.gridOffsetX, fallback.gridOffsetX),
  gridOffsetY: keepDefined(values?.gridOffsetY, fallback.gridOffsetY),
  staggerRows: keepDefined(values?.staggerRows, fallback.staggerRows),
  staggerTwoSides: keepDefined(values?.staggerTwoSides, fallback.staggerTwoSides),
  spreadScrews: keepDefined(values?.spreadScrews, fallback.spreadScrews),
  limitGridToMax: keepDefined(values?.limitGridToMax, fallback.limitGridToMax),
  limitGridDiagonalAtGrazingAngle: keepDefined(
    values?.limitGridDiagonalAtGrazingAngle,
    fallback.limitGridDiagonalAtGrazingAngle,
  ),
  gridRowsLinkedToMax: keepDefined(values?.gridRowsLinkedToMax, fallback.gridRowsLinkedToMax),
  gridColsLinkedToMax: keepDefined(values?.gridColsLinkedToMax, fallback.gridColsLinkedToMax),
  gridOffsetLinkedToMax: keepDefined(values?.gridOffsetLinkedToMax, fallback.gridOffsetLinkedToMax),
});

const serializeForComparison = (v: ScrewConnectorCalculationFormValues): string => JSON.stringify(v);

export type AutoSaveScrewConnectorCalculationReturn = {
  screwConnectorCalculation: ScrewConnectorCalculationData | null;
  isSaving: boolean;
};

export type AutoSaveScrewConnectorCalculationOptions = {
  onError?: (error: unknown) => void;
  // nothing is saved while paused (e.g. the guided tutorial fills the form with example data); on resume the form
  // is compared again with the last saved data
  paused?: boolean;
};

export function useAutoSaveScrewConnectorCalculation(
  calculation: Calculation<ScrewConnectorCalculationData> | undefined,
  control: Control<ScrewConnectorCalculationFormValues>,
  { onError, paused = false }: AutoSaveScrewConnectorCalculationOptions = {},
): AutoSaveScrewConnectorCalculationReturn {
  const watchedValues = useWatch({ control });
  const { updateCalculation } = useCalculationsApi();
  const [persistedCalculation, setPersistedCalculation] = useState(calculation);
  const [isPersisting, setIsPersisting] = useState(false);
  const onErrorRef = useRef(onError);
  const lastSerializedRef = useRef(
    calculation ? serializeForComparison(buildScrewConnectorCalculationDefaults(calculation)) : "",
  );
  const baselineIdRef = useRef(calculation?.id);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!calculation) return;
    setPersistedCalculation(calculation);
    // The baseline is the last data sent, re-read from the calculation only when another one is opened: the copies
    // pushed back by each save (optimistic update, server response) may be normalized (e.g. CLT layers null -> []),
    // and comparing the form with them would trigger a new save every time.
    if (baselineIdRef.current === calculation.id) return;
    baselineIdRef.current = calculation.id;
    lastSerializedRef.current = serializeForComparison(buildScrewConnectorCalculationDefaults(calculation));
  }, [calculation]);

  useEffect(() => {
    if (!calculation || !calculation.canEdit || paused) return;

    const fallbackValues = buildScrewConnectorCalculationDefaults(calculation);
    const nextData = mergeScrewConnectorCalculationValues(
      watchedValues as Partial<ScrewConnectorCalculationFormValues>,
      fallbackValues,
    );
    const nextSerialized = serializeForComparison(nextData);

    if (nextSerialized === lastSerializedRef.current) return;

    const timer = window.setTimeout(() => {
      lastSerializedRef.current = nextSerialized;
      setIsPersisting(true);

      const body: UpdateCalculationBody<ScrewConnectorCalculationData> = {
        name: calculation.name,
        description: calculation.description ?? null,
        data: nextData,
      };

      void updateCalculation(calculation.project?.id, calculation.id, body, {
        ...calculation,
        data: nextData,
      })
        .then((updated) => setPersistedCalculation(updated))
        .catch((err) => onErrorRef.current?.(err))
        .finally(() => setIsPersisting(false));
    }, 350);

    return () => window.clearTimeout(timer);
  }, [calculation, watchedValues, updateCalculation, paused]);

  return {
    screwConnectorCalculation: persistedCalculation?.data ?? null,
    isSaving: isPersisting,
  };
}
